import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { brand } from "../../../config/theme";
import { EmptyState } from "../../../components/ui";
import { useVehicles } from "../hooks/useVehicles";
import { useWorkshops } from "../hooks/useWorkshops";
import { useMxLabels } from "../hooks/useMxLabels";
import { useMaintenanceRecords } from "../hooks/useMaintenanceRecords";
import { useMaintenanceActions } from "../hooks/useMaintenanceActions";
import { useMaintenanceCapabilities } from "../hooks/useMaintenanceCapabilities";
import { createMaintenance, updateMaintenance } from "../services/maintenanceService";
import { MAINTENANCE_STATUSES, WORKSHOP_MODES } from "../theme";
import { costOf, dueOf, isOpen, isOverdue, matchesQuery } from "../lib";
import type { MaintenanceRecord, MaintenanceType } from "../types";
import { ErrorBlock, KpiCard, LoadingBlock, PageShell, SearchInput, SelectFilter, ToolbarButton, TruncationNotice, ViewSwitch } from "../components/kit/Primitives";
import { MaintenanceCardGrid, MaintenanceKanban, MaintenanceTable } from "../components/kit/Records";
import { MaintenanceDrawer } from "../components/kit/MaintenanceDrawer";
import MaintenanceFormModal from "../components/MaintenanceFormModal";
import { MANTENIMIENTO_BASE } from "../paths";

interface MaintenanceListSectionProps {
  /** Filter + default for the lifecycle type. Omit for the work-orders view. */
  type?: MaintenanceType;
  icon: string;
  /** Work-orders mode: kanban por estado, campos de orden y cobro. */
  workOrders?: boolean;
}

type View = "cards" | "table" | "kanban";

const VIEW_KEY = (k: string) => `mx.view.${k}`;
function readView(key: string, fallback: View): View {
  try {
    const v = localStorage.getItem(VIEW_KEY(key));
    return v === "cards" || v === "table" || v === "kanban" ? v : fallback;
  } catch {
    return fallback;
  }
}

// Programado / Solicitado / Sugerido / Órdenes de trabajo: consola con KPIs,
// filtros (búsqueda, estado, unidad, taller, vencidos), vista en tarjetas,
// tabla o tablero kanban, y panel lateral de detalle con galería.
export default function MaintenanceListSection({ type, icon, workOrders = false }: MaintenanceListSectionProps) {
  const L = useMxLabels();
  const { t } = L;
  const navigate = useNavigate();
  const sectionKey = type ?? "ordenes";
  const { capabilities } = useMaintenanceCapabilities();
  const { vehicles, vehicleOf, labelOf } = useVehicles();
  const { workshops } = useWorkshops();
  const { rows, total, loading, loadingMore, error, reload, loadMore } = useMaintenanceRecords(type ? { type } : {});
  const actions = useMaintenanceActions(reload);

  const [view, setViewState] = useState<View>(() => readView(sectionKey, workOrders ? "kanban" : "cards"));
  const setView = (v: View) => {
    setViewState(v);
    try {
      localStorage.setItem(VIEW_KEY(sectionKey), v);
    } catch {
      /* sin almacenamiento: sólo en memoria */
    }
  };
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [mode, setMode] = useState("");
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState<null | { record?: MaintenanceRecord | null }>(null);

  const filtered = useMemo(() => {
    const out = rows.filter(
      (r) =>
        (!status || r.status === status) &&
        (!vehicleId || r.vehicleId === vehicleId) &&
        (!mode || (r.workshopMode || "propio") === mode) &&
        (!onlyOverdue || isOverdue(r)) &&
        matchesQuery(r, query, labelOf(r.vehicleId))
    );
    // Vencidos primero; luego abiertos por fecha y al final los cerrados.
    return out.sort((a, b) => {
      const rank = (r: MaintenanceRecord) => (isOverdue(r) ? 0 : isOpen(r) ? 1 : 2);
      const d = rank(a) - rank(b);
      if (d) return d;
      const da = Date.parse(dueOf(a) || a.completedDate || a.lastMaintenanceDate || "") || 0;
      const db = Date.parse(dueOf(b) || b.completedDate || b.lastMaintenanceDate || "") || 0;
      return rank(a) === 2 ? db - da : da - db;
    });
  }, [rows, status, vehicleId, mode, onlyOverdue, query, labelOf]);

  const kpis = useMemo(() => {
    const open = rows.filter(isOpen);
    return {
      open: open.length,
      overdue: rows.filter((r) => isOverdue(r)).length,
      inProgress: rows.filter((r) => r.status === "en_proceso").length,
      completed: rows.filter((r) => r.status === "completado").length,
      cost: rows.filter((r) => r.status === "completado").reduce((s, r) => s + costOf(r), 0),
    };
  }, [rows]);

  const selected = rows.find((r) => r.id === selectedId) || null;
  const hasFilters = !!(query || status || vehicleId || mode || onlyOverdue);
  const clearFilters = () => {
    setQuery("");
    setStatus("");
    setVehicleId("");
    setMode("");
    setOnlyOverdue(false);
  };

  const save = useCallback(
    async (payload: Partial<MaintenanceRecord>, id?: string) => {
      if (id) await updateMaintenance(id, payload);
      else await createMaintenance(payload);
      await reload();
    },
    [reload]
  );

  const titleKey = workOrders ? "ordenes" : type ?? "ordenes";
  const viewOptions = [
    { id: "cards" as const, icon: "ri-layout-grid-line", label: t("mantenimiento.ui.views.cards") },
    { id: "table" as const, icon: "ri-table-line", label: t("mantenimiento.ui.views.table") },
    ...(workOrders ? [{ id: "kanban" as const, icon: "ri-kanban-view", label: t("mantenimiento.ui.views.kanban") }] : []),
  ];
  const effectiveView: View = view === "kanban" && !workOrders ? "cards" : view;

  return (
    <PageShell
      icon={icon}
      title={t(`mantenimiento.listTitles.${titleKey}`)}
      subtitle={t(`mantenimiento.listSubtitles.${titleKey}`)}
      actions={
        <>
          <ToolbarButton icon="ri-refresh-line" label={t("mantenimiento.ui.refresh")} onClick={reload} spinning={loading} />
          {capabilities.canManageRecords ? <ToolbarButton primary icon="ri-add-line" label={t("mantenimiento.newRecord")} onClick={() => setForm({ record: null })} /> : null}
        </>
      }
      toolbar={
        <>
          <SearchInput value={query} onChange={setQuery} placeholder={t("mantenimiento.ui.search")} />
          {effectiveView !== "kanban" ? (
            <SelectFilter value={status} onChange={setStatus} label={t("mantenimiento.fields.status")} options={[{ value: "", label: t("mantenimiento.ui.allStatuses") }, ...MAINTENANCE_STATUSES.map((s) => ({ value: s, label: L.status(s) }))]} />
          ) : null}
          <SelectFilter value={vehicleId} onChange={setVehicleId} label={t("mantenimiento.fields.vehicle")} options={[{ value: "", label: t("mantenimiento.ui.allVehicles") }, ...vehicles.map((v) => ({ value: v.id, label: v.label }))]} />
          <SelectFilter value={mode} onChange={setMode} label={t("mantenimiento.fields.workshopMode")} options={[{ value: "", label: t("mantenimiento.ui.allModes") }, ...WORKSHOP_MODES.map((m) => ({ value: m, label: L.mode(m) }))]} />
          <button
            type="button"
            onClick={() => setOnlyOverdue((v) => !v)}
            aria-pressed={onlyOverdue}
            className={`inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-sm ${onlyOverdue ? "border-error bg-error/10 text-error" : "border-line bg-card text-content-secondary hover:bg-subtle"}`}
          >
            <i className="ri-alarm-warning-line" />
            {t("mantenimiento.ui.onlyOverdue")}
          </button>
          {hasFilters ? (
            <button type="button" onClick={clearFilters} className="h-9 px-2 text-sm text-primary-500 hover:underline">
              {t("mantenimiento.ui.clearFilters")}
            </button>
          ) : null}
          <span className="ml-auto text-xs text-content-muted">{t("mantenimiento.ui.results", { count: filtered.length })}</span>
          <ViewSwitch value={effectiveView} onChange={setView} options={viewOptions} />
        </>
      }
    >
      <TruncationNotice shown={rows.length} total={total} onLoadMore={loadMore} loading={loadingMore} />
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <KpiCard icon="ri-alarm-warning-line" tone="error" label={t("mantenimiento.ui.kpi.overdue")} value={kpis.overdue} loading={loading} onClick={() => setOnlyOverdue(true)} />
        <KpiCard icon="ri-inbox-line" tone="warning" label={t("mantenimiento.ui.kpi.openOrders")} value={kpis.open} loading={loading} />
        <KpiCard icon="ri-tools-line" tone="info" label={t("mantenimiento.ui.kpi.inShop")} value={kpis.inProgress} loading={loading} onClick={() => setStatus("en_proceso")} />
        <KpiCard icon="ri-checkbox-circle-line" tone="success" label={t("mantenimiento.ui.kpi.completed")} value={kpis.completed} loading={loading} onClick={() => setStatus("completado")} />
        <KpiCard icon="ri-money-dollar-circle-line" tone="primary" label={t("mantenimiento.totalCost")} value={new Intl.NumberFormat(L.locale, { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(kpis.cost)} loading={loading} />
      </div>

      {error ? (
        <ErrorBlock message={error} onRetry={reload} />
      ) : loading && !rows.length ? (
        <LoadingBlock />
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line bg-card">
          <EmptyState
            icon={icon}
            title={hasFilters ? t("mantenimiento.ui.emptyFiltered") : t("mantenimiento.ui.emptyTitle")}
            description={hasFilters ? undefined : t("mantenimiento.ui.emptyHint")}
            action={hasFilters ? <button type="button" onClick={clearFilters} className="text-sm text-primary-500 hover:underline">{t("mantenimiento.ui.clearFilters")}</button> : undefined}
          />
        </div>
      ) : effectiveView === "table" ? (
        <MaintenanceTable rows={filtered} vehicleOf={vehicleOf} onOpen={(r) => setSelectedId(r.id)} showType={!type} />
      ) : effectiveView === "kanban" ? (
        <>
          <p className="mb-2 text-xs text-content-muted">
            <i className="ri-drag-move-2-line mr-1" />
            {t("mantenimiento.ui.kanban.dropHint")}
          </p>
          <MaintenanceKanban
            rows={filtered}
            vehicleOf={vehicleOf}
            onOpen={(r) => setSelectedId(r.id)}
            canManage={capabilities.canManageRecords}
            onTransition={actions.transition}
            onInvalidMove={() => Swal.fire({ title: t("mantenimiento.ui.kanban.invalidMove"), icon: "info", confirmButtonColor: brand.primary, timer: 2200 })}
          />
        </>
      ) : (
        <MaintenanceCardGrid rows={filtered} vehicleOf={vehicleOf} onOpen={(r) => setSelectedId(r.id)} canManage={capabilities.canManageRecords} onTransition={actions.transition} showType={!type} />
      )}

      {selected ? (
        <MaintenanceDrawer
          record={selected}
          vehicle={vehicleOf(selected.vehicleId)}
          onClose={() => setSelectedId(null)}
          canManage={capabilities.canManageRecords}
          canCharge={capabilities.canCharge}
          onTransition={actions.transition}
          onPdf={actions.workOrderPdf}
          onCharge={actions.charge}
          onEdit={(r) => setForm({ record: r })}
          onDelete={async (r) => {
            if (await actions.remove(r)) setSelectedId(null);
          }}
          onOpenVehicle={(id) => navigate(`${MANTENIMIENTO_BASE}/historial?vehicle=${encodeURIComponent(id)}`)}
        />
      ) : null}

      {form ? (
        <MaintenanceFormModal
          record={form.record}
          defaultType={type}
          workOrder={workOrders}
          vehicles={vehicles}
          workshops={workshops}
          onClose={() => setForm(null)}
          onSubmit={save}
        />
      ) : null}
    </PageShell>
  );
}
